import { Typography } from '@mui/material';
import { STORAGE_KEY } from 'constants/global';
import { daysOfWeekOptions } from 'features/create-request/helpers';
import { REQUEST_DETAIL_DATA } from 'models/create-request';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { getChildrenListAction } from 'store/reducers/parent/actionTypes';
import { convertDobToAge, formatDate, getLocalStorage } from 'utils';
import './styles.css';

export const ConvertPDFtoHTML = () => {
  const dispatch = useAppDispatch();
  const { parent } = useAppSelector((state) => state.auth);
  const { parentSignature } = useAppSelector(
    (state) => state.baseRequest.overallDataSubmit,
  );
  const childrenSelected = JSON.parse(
    getLocalStorage(STORAGE_KEY.CREATE_REQUEST_DATA_FILLED),
  )?.requestDetailData?.children;

  const [baseRequestDetail, setBaseRequestDetail] =
    useState<REQUEST_DETAIL_DATA>(null);

  const timingDates = useMemo(() => {
    if (!baseRequestDetail) return [];

    return baseRequestDetail?.dayOfWeeks
      ?.map((apiDay) =>
        daysOfWeekOptions.find((option) => option.id === Number(apiDay)),
      )
      .filter(Boolean);
  }, [baseRequestDetail]);

  const estHoursPerWeek = useMemo(() => {
    if (!baseRequestDetail) return 0;

    // Calculate difference in milliseconds
    const differenceMilliseconds = moment(baseRequestDetail.endTime).diff(
      moment(baseRequestDetail.startTime),
    );

    // Convert milliseconds to hours
    const totalHours = Number(
      (differenceMilliseconds / 1000 / 60 / 60).toFixed(2),
    );

    const totalDaysInWeek =
      baseRequestDetail.dayOfWeeks.length > 7
        ? 7
        : baseRequestDetail.dayOfWeeks.length;

    return totalHours * totalDaysInWeek;
  }, [baseRequestDetail]);

  const getAddress = () => {
    const parentAddress = baseRequestDetail?.address;
    if (!parentAddress) return 'None';
    return `${parentAddress?.unit_number}, ${parentAddress?.block_number} ${parentAddress?.street}, Singapore ${parentAddress?.postal_code}`;
  };

  useEffect(() => {
    dispatch(getChildrenListAction());

    const requestData = JSON.parse(
      getLocalStorage(STORAGE_KEY.CREATE_REQUEST_DATA_FILLED),
    );
    setBaseRequestDetail(requestData.requestDetailData);
    // eslint-disable-next-line
  }, [dispatch]);

  if (!baseRequestDetail || !parent) return;

  return (
    <div id="terms-pdf" className="wrapper-pdf">
      {/* PAGE 1 */}
      <div className="page">
        <h3 style={{ textAlign: 'left', fontSize: '20px' }}>
          Aunty Concierge Service Agreement
        </h3>
        <p>
          The service agreement (this "<strong>Agreement</strong>") is made on
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: '1px solid black',
            paddingBottom: '4px',
            marginBottom: '15px',
            width: 'calc(100% - 20px)',
          }}
        >
          <span>
            <strong>Date:</strong>
            <span style={{ paddingLeft: '10px' }}>
              {moment().format('DD/MM/YYYY')}
            </span>
          </span>
          <span>between:</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: '1px solid black',
            paddingBottom: '4px',
            marginBottom: '15px',
            width: 'calc(100% - 20px)',
          }}
        >
          <span>
            <strong>Name:</strong>
            <span style={{ paddingLeft: '10px' }}>{parent.full_name}</span>
          </span>
          <span>of</span>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: '1px solid black',
            paddingBottom: '4px',
            marginBottom: '15px',
            width: 'calc(100% - 20px)',
          }}
        >
          <span>
            <strong>Contact Number:</strong>
            <span style={{ paddingLeft: '10px' }}>{parent.phone_number}</span>
          </span>
          <span></span>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: '1px solid black',
            paddingBottom: '4px',
            marginBottom: '15px',
            width: 'calc(100% - 20px)',
          }}
        >
          <span>
            <strong>Email Address:</strong>
            <span style={{ paddingLeft: '10px' }}>{parent.email}</span>
          </span>
          <span></span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexDirection: 'column',
            borderBottom: '1px solid black',
            paddingBottom: '4px',
            marginBottom: '15px',
            width: 'calc(100% - 20px)',
          }}
        >
          <span>
            <strong>Address:</strong>
          </span>
          <span style={{ paddingLeft: '10px', paddingTop: '2px' }}>
            {getAddress()}
          </span>
        </div>
        <p>
          (the "<strong>Client</strong>");
        </p>
        <p>
          <strong>Company:</strong> Aunty Pte. Ltd., a company incorporated in
          Singapore whose registered address is at 7 Temasek Blvd, #02-08,
          Singapore 038702 (the "<strong>Company</strong>"), each a "
          <strong>Party</strong>" and collectively referred to as the "
          <strong>Parties</strong>".
        </p>
        <div className="section-header">(i) Definitions</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            "<strong>Agreement</strong>" shall refer to this Agreement;
          </p>
          <p data-counter-item>
            "<strong>Commencement Date</strong>" has the meaning set out in
            Clause 6.1;
          </p>
          <p data-counter-item>
            "<strong>Term</strong>" means the period commencing from the
            Commencement Date, unless terminated earlier pursuant to the
            provisions of this Agreement;
          </p>
          <p data-counter-item>
            "<strong>Trial</strong>" has the meaning set out in Clause 4.1;
          </p>
          <p data-counter-item>
            "<strong>2nd Trial</strong>" has the meaning set out in Clause 4.3;
          </p>
          <p data-counter-item>
            "<strong>Service Fee</strong>" means the fee payable by the Client
            as set out in paragraph 1 of the Schedule.
          </p>
          <p data-counter-item>
            "<strong>Sitter</strong>" means an independent contractor hired or
            to be hired by the Company.
          </p>
          <p data-counter-item>
            "<strong>Search Fee</strong>" means the charge imposed for the
            administrative costs associated with managing the service
          </p>
        </ol>
        <div className="section-header">(ii) Interpretations</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Any reference in this Agreement to a Clause, Sub-Clause or Schedule
            (if any) shall be a reference to a Clause, Sub-Clause or Schedule to
            this Agreement.
          </p>
          <p data-counter-item>
            Words importing neutral gender shall include the feminine and
            masculine genders and vice versa.
          </p>
          <p data-counter-item>
            References to dollars or S$ are to the lawful currency from time to
            time of Singapore.
          </p>
          <p data-counter-item>
            Any reference to any statute or statutory provision includes a
            reference to that statute or statutory provision as from time to
            time amended, extended or re-enacted and shall include all by-laws,
            instruments, orders, rules and regulation made thereunder.
          </p>
          <p data-counter-item>
            Where there is any inconsistency between the definitions set out in
            this Clause and the definitions set out in any Schedule (if any),
            then for the purposes of construing such Clause or Schedule, the
            definitions set out in such Clause or Schedule shall prevail.
          </p>
          <p data-counter-item>
            The headings in this Agreement do not affect its interpretation.
          </p>
          <p data-counter-item>
            Any schedule to this Agreement shall take effect as if set out in
            this Agreement and references to this Agreement shall include its
            schedules.
          </p>
        </ol>
        <div className="section-header">
          (iii) General Responsibilities of the Client
        </div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            The general responsibilities of the Client include the following:
          </p>
          <p data-counter-item>
            a) making payment of the Service Fee to the Company on time;
          </p>
          <p data-counter-item>
            b) ensuring that the necessary arrangements have been put in place
            for the Sitter to efficiently carry out the Service, which includes
            but is not limited to ensuring that the Sitter works in a safe,
            pleasant and friendly environment as the Company may in its sole
            discretion determine; and
          </p>
          <p data-counter-item>
            c) reimbursement of any reasonable costs incurred by the Sitter from
            the provision of the Service, subject to the Sitter providing valid
            documentation.
          </p>
        </ol>
        <div className="section-header">(iv) Fees</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Within one (1) week of signing this Agreement, the Client shall pay
            the Company a non-refundable fee of
          </p>
          <p data-counter-item>
            S$50.00 in consideration for work done by the Company to conduct
            pre-screening processes for a Sitter ("Search Fee"). In
            consideration for providing the Service, the Client shall pay to the
            Company the Search Fee as set out in this Agreement. In the event
            that there is no sitter profile surfaced, the Search Fee will be
            refunded to the Client.
          </p>
          <p data-counter-item>
            If there are changes to bookings, any remaining credits for unused
            services will be rolled over for subsequent bookings. The Service
            Fees and the Sitters Fees are strictly non-refundable.
          </p>
        </ol>
        <div className="page-number">Page 1 of 5</div>
      </div>
      {/* PAGE 2 */}
      <div className="page">
        <div className="section-header">(v) Trial</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Upon the Client's payment of the Search Fee, the Company shall
            provide the Client with one (1) trial (of minimum three (3) hours
            duration) during which the Client may access the Service with the
            Sitter ("
            <strong>Trial</strong>"). The Company shall inform the parent within
            seven (7) business days once the Sitter is found upon. The Sitter
            Profile shall be provided at our discretion.
          </p>
          <p data-counter-item>
            The Trial must be utilised seven (7) business days upon informing
            the Client of the details of the Sitter allocated from the Company's
            receipt of the Service Fee.
          </p>
          <p data-counter-item>
            If after the Trial the Client is not satisfied with the relevant
            Sitter, the Client may by no later than one (1) week after the
            Trial, request the Company allocate another Sitter, and arrange
            another Trial. If after the second Trial the Client is still not
            satisfied, this Agreement shall terminate although the Client shall
            remain liable to pay any amounts due and owing as set out in the
            Schedule. For the avoidance of doubt, the Client shall only be
            entitled to a maximum of two (2) Trials and may only request an
            alternative Sitter once. In the event that midterm the Client wants
            to change Sitter, both parties' contract will terminate at one
            month's notice.
          </p>
          <p data-counter-item>
            After the trial session with the Sitter, the Client has 7 calendar
            days to decide if they would like to continue with the Sitter's
            services on an ongoing basis. If the Client does not confirm their
            decision within this 7 day period, the Sitter may be made available
            for other opportunities. Any continued availability of the
            babysitter after this period cannot be guaranteed.
          </p>
        </ol>
        <div className="section-header">(vi) Timesheet</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            The Client is to approve and sign off on the Sitter's time sheet (in
            the form that the Company may prescribe from time to time) after
            each scheduled work session, indicating the number of hours worked
            during the relevant day.
          </p>
        </ol>
        <div className="section-header">(vii) Term and Termination</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            This Agreement shall take effect from the date of Agreement, or such
            other date mutually agreed by the Parties (the "
            <strong>Commencement Date</strong>") and, unless terminated earlier
            in accordance with the provisions of this Agreement, shall continue
            in force for the Term.
          </p>
          <p data-counter-item>
            Either Party may terminate this Agreement by giving the other Party
            at least one (1) month's prior written notice at any time.
          </p>
        </ol>
        <div className="section-header">(viii) Entire Agreement</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            The Parties agree that this Agreement constitutes the entire
            agreement between them and supersedes all previous agreements,
            understandings and arrangements between them, whether in writing or
            oral in respect of its subject matter.
          </p>
          <p data-counter-item>
            Each Party acknowledges that it has not entered into this Agreement
            in reliance on, and shall have no remedies in respect of, any
            representation or warranty that is not expressly set out in this
            Agreement, except in the case of fraudulent misrepresentation.
          </p>
        </ol>
        <div className="section-header">(ix) Severability</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Each Clause of this Agreement is severable and distinct from the
            others. If any Clause in this Agreement (or part thereof) is or
            becomes illegal, invalid or unenforceable under applicable law, that
            would be legal, valid and enforceable if the Clause or some part of
            it was deleted or modified (or the duration of the relevant Clause
            reduced):
          </p>
          <p data-counter-item>
            a) the relevant Clause (or part thereof) will apply with such
            deletion or modification as may be required to make it legal, valid
            and enforceable; and
          </p>
          <p data-counter-item>
            b) without limiting the foregoing, in such circumstances the Parties
            will promptly and in good faith seek to negotiate a replacement
            provision consistent with the original intent of this Agreement as
            soon as possible.
          </p>
        </ol>
        <div className="section-header">
          (x) Waiver and Limitation of Liability
        </div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            No failure, delay or omission by either Party in exercising any
            right, power or remedy provided by law or under this Agreement shall
            operate as a waiver of that right, power or remedy, nor shall it
            preclude or restrict any future exercise of that or any other right
            or remedy. No single or partial exercise of any right, power or
            remedy provided by law or under this Agreement shall prevent any
            future exercise of it or the exercise of any other right, power or
            remedy.
          </p>
          <p data-counter-item>
            To the extent applicable by law, the Company is not liable for any
            loss, damages, costs (whether in contract or tort) or otherwise,
            death or personal injury that may arise from or out of connection of
            an engagement with the Client or Sitter.
          </p>
          <p data-counter-item>
            For the avoidance of doubt, nothing in Clause 2 limits or excludes
            liability for death or personal injury caused by negligence or
            fraud.
          </p>
        </ol>
        <div className="page-number">Page 2 of 5</div>
      </div>
      {/* PAGE 3 */}
      <div className="page">
        <ol
          className="numbering numbering-custom-counter"
          style={{ marginTop: 0 }}
          start={3}
        >
          <p>
            4.&nbsp;&nbsp;&nbsp; In no event will the Company's liability for
            claims or damages suffered or incurred by the Client arising under
            or in connection with this Agreement (whether in contract, tort,
            misrepresentation, negligence, strict liability in tort or by
            statute or otherwise) be greater than the total sum of the Service
            Fee paid by the Client under this Agreement.
          </p>
        </ol>
        <div className="section-header">(xi) Non-Solicitation</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            During the Term of this Agreement and for a period of six (6) months
            from the termination of this Agreement, neither the Client nor any
            person connected with the Client (including but not limited to
            immediate family members of the Client) shall in any location or
            capacity, whether for his or her own account or for the account of
            any other person or entity (other than the Company), directly or
            indirectly, solicit the services of the Sitter or services similar
            to that provided by the Sitter under this Agreement (inclusive of
            the Trials). As used in this Agreement, "solicit" means the
            initiation, whether directly or indirectly, of any contact or
            communication of any kind whatsoever, for the express or implicit
            purpose of inviting, encouraging or requesting a Sitter to:
          </p>
          <p style={{ paddingLeft: '25px' }}>
            a) engage in any capacity whatsoever for the provision of the
            Service and other functions or responsibilities incidental or
            closely connected to it directly to the Client or any other person;
          </p>
          <p style={{ paddingLeft: '25px' }}>
            b) provide babysitting services outside the scope of this Agreement
            to any other person;
          </p>
          <p style={{ paddingLeft: '25px' }}>
            c) otherwise discontinue, change or reduce the Sitter's existing
            independent contractor relationship with the Company.
          </p>
          <p data-counter-item>
            In the event of default of Clause 1 by the Client, the Client shall
            be liable to indemnify the Company to a sum of S$2,000.00. In
            addition, notwithstanding clause 7.2 above, the Company may at its
            sole discretion terminate this Agreement with immediate effect.
          </p>
          <p data-counter-item>
            The Client agrees that the scope of prohibitions set out in Clause 1
            to 10.2 are reasonable and necessary to protect the Company. If any
            court or other tribunal having jurisdiction to determine the
            validity, scope or enforceability of this Clause 10, determines that
            if strictly applied any provision hereof would be invalid or
            unenforceable, the relevant scope of restrictions and/or the time
            restrictors set forth in Clause 10.1 to 10.2 hereof shall be
            modified to the extent necessary to make such restrictions, as
            modified, valid and enforceable to the fullest extent permitted by
            law.
          </p>
          <p data-counter-item>
            Aunty App and Aunty Concierge services provided by Aunty are
            distinct and separate services. Clients engaging in Aunty Concierge
            services will be subject to terms and conditions unique to that
            service. Sitters assigned to clients through the Concierge service
            will be exclusive to that placement for a duration of three (3)
            months. During this exclusivity period, the assigned sitter cannot
            be engaged via the Aunty App or any other form of direct or indirect
            engagement outside the Concierge service. Any attempt to engage the
            assigned sitter outside the Concierge service within the specified
            three (3) month period will be considered a breach of contract.
          </p>
        </ol>
        <div className="section-header">(xii) No Partnership or Agency</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            The Parties acknowledge that nothing in this Agreement constitutes a
            relationship of employer and employee, principal and agent,
            partnership or joint venture between the Company and the Client. The
            Client acknowledges that it does not have the authority to enter
            into binding agreements of any nature or make any representations or
            warranties on the Company's or any Sitter's behalf and agrees not to
            misrepresent to any third party any such authority.
          </p>
          <p data-counter-item>
            The Client acknowledges that each Sitter is an independent
            contractor of the Company, and each Sitter shall not at any time
            have any authority to make any agreements or representations on the
            Company's behalf. Further, the Client acknowledges that each Sitter
            shall be solely responsible for the provision of the Service, and
            shall at all times be free from the supervision, control and
            direction of the Company.
          </p>
        </ol>
        <div className="section-header">(xiii) Assignment</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Subject to Clause 2, all rights and obligations hereunder are
            personal to the Parties and a Party may not assign, transfer or
            dispose of all or part of its rights or obligations under this
            Agreement (including any cause of action arising in connection with
            this Agreement) without the prior written consent of the other
            Party.
          </p>
          <p data-counter-item>
            Notwithstanding Clause 1, the Company may assign or transfer all
            (and not some) of its rights, benefits, interests and/or obligations
            under this Agreement to its affiliates by providing a written notice
            of such assignment or transfer to the other Party prior to such
            assignment or transfer.
          </p>
        </ol>
        <div className="section-header">(xiv) Counterparts</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            This Agreement may be executed in counterparts, each of which will
            constitute an original and all of which duly executed shall be taken
            together to constitute one and the same instrument and shall enter
            into force on the Commencement Date. Each Party may enter into this
            Agreement by signing any such counterpart. Signatures may be
            exchanged by post or email, with original
          </p>
        </ol>
        <div className="page-number">Page 3 of 5</div>
      </div>
      {/* PAGE 4 */}
      <div className="page">
        <span style={{ marginTop: 0 }}>
          signatures to follow. Each Party agrees to be bound by its own
          electronic signature and that it accepts the electronic signature of
          the other Parties.
        </span>
        <div className="section-header">(xv) Third Party Rights</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Unless expressly provided to the contrary in this Agreement, a
            person who is not a Party herein has no right under the Contracts
            (Rights of Third Parties) Act 2001 of Singapore to enforce or to
            enjoy the benefit of any term of this Agreement.
          </p>
        </ol>
        <div className="section-header">(xvi) Whole Agreement</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            This Agreement contains the whole agreement between the Parties
            relating to the subject matter of this Agreement at the date of this
            Agreement to the exclusion of any terms implied by law which may be
            excluded by contract and supersedes any previous written or oral
            agreement between the Parties in relation to the matters dealt with
            in this Agreement.
          </p>
        </ol>
        <div className="section-header">(xvii) Remedies</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            Subject to the terms of this Agreement, the rights and remedies
            contained in this Agreement are cumulative and not exclusive of any
            rights or remedies provided by law, equity or otherwise.
          </p>
        </ol>
        <div className="section-header">
          (xviii) Remedies Governing Law and Jurisdiction
        </div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            In the event of any dispute or claim arising out of, in connection
            with, or in relation to the interpretation, performance,
            non-performance, validity or breach of this Agreement or otherwise
            arising out of, or in any way related to this Agreement or the
            transactions contemplated thereby, including any claim based on
            contract, tort, statute or constitution (collectively, "
            <strong>Disputes</strong>"), the Parties shall attempt to solve such
            Dispute through amicable negotiation which shall take place within
            30 (thirty) calendar days from the date such disagreement was
            communicated to the other Party in writing (the "
            <strong>Dispute Notice</strong>").
          </p>
          <p data-counter-item>
            If a Dispute has not been resolved by negotiation under Clause 1
            within 30 (thirty) calendar days of the Dispute Notice, either Party
            may subsequently initiate proceedings against the other Party. Any
            disputes arising out of or in connection with this Agreement,
            including disputes on its conclusion, binding effects, amendment and
            termination, shall be subject to the exclusive jurisdiction of the
            courts in Singapore.
          </p>
          <p data-counter-item>
            This Agreement shall be governed by and construed in accordance with
            the laws of the Republic of Singapore.
          </p>
        </ol>
        <div className="section-header">(xix) Service Fee</div>
        <ol
          className="numbering numbering-custom-counter"
          start={1}
          style={{ marginBottom: 50 }}
        >
          <p data-counter-item>
            For the Term of the Agreement, the hourly fees payable by the Client
            is:
          </p>
        </ol>
        <table>
          <tbody>
            <tr>
              <th>Plans</th>
              <th>No. of Hrs</th>
              <th>Rate</th>
            </tr>
            <tr>
              <td>Basic Nanny Plan</td>
              <td>~ 10</td>
              <td>$35/hr</td>
            </tr>
            <tr>
              <td>Enhanced Nanny Plan</td>
              <td>~ 20</td>
              <td>$30/hr</td>
            </tr>
            <tr>
              <td>Premium Nanny Plan</td>
              <td>~ 50</td>
              <td>$28/hr</td>
            </tr>
            <tr>
              <td>Ultimate Nanny Plan</td>
              <td>~ 100</td>
              <td>$25/hr</td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr>
              <th>Surcharges</th>
              <th>Rate</th>
            </tr>
            <tr>
              <td>
                Before 8am &amp; after 6pm
                <br />
                (Weekdays &amp; Weekends)
              </td>
              <td>$2/hr</td>
            </tr>
            <tr>
              <td>Weekends</td>
              <td>$2/hr</td>
            </tr>
            <tr>
              <td>Singapore Public Holidays</td>
              <td>$5/hr</td>
            </tr>
            <tr>
              <td>Additional Child (&gt;1 kid)</td>
              <td>$5/hr</td>
            </tr>
          </tbody>
        </table>
        <ol className="numbering numbering-custom-counter" start={2}>
          <p data-counter-item>
            For sits starting or ending after 11PM or before 7AM, commuting
            expenses will be incurred as necessary. The Client will be billed
            for these commuting expenses at the end of each month.
          </p>
          <div>
            <p data-counter-item>
              The Company shall, before either of the following events:
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: 15,
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span>
                a) the Trial or the 2nd Trial, as the case may be ("First
                Payment"); and
              </span>
              <span>
                b) the end of each subsequent calendar month for which the
                Sitter rendered the Service ("Subsequent Payment"); provide the
                Client with a digital invoice for payment of the Service Fee to
                be made in accordance with paragraph 2 of this Schedule.
              </span>
            </div>
          </div>
          <span style={{ paddingBottom: 5 }}>
            During the Trial, the hourly rate will be set at $35. An additional
            surcharge of $5 per hour will be applied if the trial request is
            made within 48 hours. A surcharge of $5 per hour will also be
            applied per additional kid. Subsequent payments will be calculated
            by deducting from the agreed-upon package.
          </span>
          <p data-counter-item>
            For sits located within Sentosa, a supplementary fee of $20 per day
            will be incurred. This surcharge accounts for increased travel time,
            additional transit and the entrance costs associated with accessing
            the island.
          </p>
          <p data-counter-item>
            In the event that the job is cancelled with less than 24 hours'
            notice before the scheduled start time of the trial, a penalty of 1
            hour of the hourly rate will be applied. This penalty is intended to
            compensate the sitter for their commitment and potential lost
            opportunities.
          </p>
          <p data-counter-item>
            The Sitter's responsibility extends to all assigned children
            throughout the session, regardless of whether a child is actively
            requiring care at any given time (e.g., during naps or independent
            play). As such, the total
          </p>
        </ol>
        <div className="page-number">Page 4 of 5</div>
      </div>
      {/* PAGE 5 */}
      <div className="page">
        <ol
          className="numbering numbering-custom-counter"
          style={{ marginTop: 0 }}
          start={7}
        >
          <p>
            hourly rate remains fixed across each sit and cannot be split or
            prorated based on individual child activity.
          </p>
          <p>
            7. &nbsp;&nbsp;&nbsp;In the event this Agreement is terminated under
            Clause 6.2, the Client will not be entitled to any refunds of the
            Service Fee already paid.
          </p>
          <p>
            8. &nbsp;&nbsp;&nbsp;The validity period for all purchased packages
            is six months from the date of purchase. It is the Client's
            responsibility to use the services within this timeframe.
          </p>
        </ol>
        <div className="section-header">(xx) Payment of the Service Fee</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            The Service Fee shall be paid on a prepayment basis, and no later
            than seven (7) working days after the Client receives the invoices
            for the First Payment and Subsequent Payment accordingly.
          </p>
          <p data-counter-item>
            All Service Fees shall be paid in Singapore dollars (S$). The
            Service Fee is exclusive of goods and services tax (GST), which if
            applicable shall be payable by the Client at the rate and in the
            manner from time to time prescribed by law.
          </p>
          <p data-counter-item>
            If the Client fails to pay the Service Fee in full by the stipulated
            deadline set out in paragraph 20.1 of this agreement, the Company
            may, without limiting its other rights, charge interest on such
            outstanding sums at five per cent (5%) per annum from the stipulated
            deadline to the date of payment of such outstanding sums.
            Non-payment will give basis to terminate with immediate effect.
          </p>
          <p data-counter-item>
            Amounts payable to the Company under this Agreement shall, unless
            otherwise notified by the Company to the Client in writing in
            accordance with this Agreement, be made to:
          </p>
        </ol>
        <div className="payment-box">
          <div>
            <p className="payment-box-header">
              <strong style={{ color: 'white' }}>Payment Details</strong>
            </p>
            <hr style={{ margin: 0 }} />
            <p>
              <strong>Name:</strong> DBS Bank
              <br />
              <strong>Account Number:</strong> 0721149281
              <br />
              <strong>PayNow:</strong> UEN 202117870K
              <br />
              <strong>Account Number:</strong> Aunty Pte. Ltd.
            </p>
          </div>
        </div>
        <ol className="numbering numbering-custom-counter" start={5}>
          <p>
            5. &nbsp;&nbsp;&nbsp;Clients are encouraged to make payments via
            PayNow to avoid additional charges. If the client chooses to make a
            payment using credit card methods, a processing fee of 4% of the
            total payment amount will be applied. This fee covers the additional
            costs associated with card transactions. By selecting a card payment
            method, the client agrees to the added fee and acknowledges that it
            will be included in their final payment total.
          </p>
        </ol>
        <div className="section-header">(xxi) Booking Form</div>
        <ol className="numbering numbering-custom-counter" start={1}>
          <p data-counter-item>
            The Client acknowledges that by booking a Sitter through Aunty
            Concierge for the specified Start Date and Service Period, the
            Sitter will be prioritized for the Client's job and this ensures
            that the Sitter will not be assigned to other clients during the
            Service Period. In the event of a service pause, the Client
            understands and agrees that the Sitter may be reassigned to other
            jobs. Aunty does not guarantee the availability of the same Sitter
            when the services resume.
          </p>
          <p data-counter-item>
            In the event that the assigned Sitter becomes unavailable to provide
            services due to unforeseen circumstances, Aunty will provide a
            suitable replacement Sitter.
          </p>
        </ol>
        <table>
          <tbody>
            <tr>
              <th>Requirements</th>
              <th>To be filled in by Client</th>
            </tr>
            <tr>
              <td>Number of Kids</td>
              <td>
                <Typography variant="Small_Body_12">
                  {childrenSelected?.length}
                </Typography>
              </td>
            </tr>
            <tr>
              <td>Age of Kids</td>
              <td>
                <Typography variant="Small_Body_12">
                  {childrenSelected
                    ?.map((child: any) => convertDobToAge(child?.date_of_birth))
                    ?.join(', ')}
                </Typography>
              </td>
            </tr>
            <tr>
              <td>Child's Date of Birth</td>
              <td>
                <Typography variant="Small_Body_12">
                  {childrenSelected
                    ?.map((child: any) =>
                      moment(child?.date_of_birth).format('DD/MM/YYYY'),
                    )
                    ?.join(', ')}
                </Typography>
              </td>
            </tr>
            <tr>
              <td>Start Date</td>
              <td>
                <Typography variant="Small_Body_12">
                  {moment(baseRequestDetail?.startDate).format('DD/MM/YYYY')}
                </Typography>
              </td>
            </tr>
            <tr>
              <td>Service Period</td>
              <td>
                <Typography variant="Small_Body_12">
                  {moment(baseRequestDetail?.endDate).format('DD/MM/YYYY')}
                </Typography>
              </td>
            </tr>
            <tr>
              <td>
                Estimated No. of
                <br />
                Total Required
                <br />
                Hours (per week)
              </td>
              <td>
                <Typography variant="Small_Body_12">
                  {estHoursPerWeek}
                </Typography>
              </td>
            </tr>
            <tr>
              <td>Dates and Timings</td>
              <td>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {timingDates?.map((day, index) => (
                      <>
                        <Typography variant="Small_Body_12" key={day.id}>
                          {day.fullName}
                          {index + 1 < timingDates.length && ', '}
                        </Typography>
                      </>
                    ))}
                  </div>
                  <Typography variant="Small_Body_12">
                    {formatDate(baseRequestDetail.startTime, {
                      format: 'hh:mm A',
                    })}
                    &nbsp;to&nbsp;
                    {formatDate(baseRequestDetail.endTime, {
                      format: 'hh:mm A',
                    })}
                  </Typography>
                </div>
              </td>
            </tr>
            <tr>
              <td>Job Requirements</td>
              <td>
                <Typography variant="Small_Body_12">
                  {baseRequestDetail?.specialRequirements}
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <strong>IN WITNESS WHEREOF</strong>, the Parties hereto have caused
          this Agreement to be executed as of the date first written above.
        </p>
        <p>
          <strong style={{ textDecoration: 'underline' }}>COMPANY</strong>
          <br />
          SIGNED by
        </p>
        <img
          src="/aunty-signature.png"
          alt="aunty-signature"
          style={{ width: '70px', height: '55px' }}
        />
        <p>
          ________________________
          <br />
          <strong> Amanda Ong, Director</strong>
          <br />
          for and on behalf of Aunty Pte. Ltd
        </p>
        <p>
          <strong>CLIENT</strong>
          <br />
          SIGNED by
        </p>
        <p>{parentSignature}</p>

        <p>
          ________________________
          <br />
          <strong>
            {' '}
            Name:{' '}
            <Typography variant="Small_Body_12">{parent.full_name}</Typography>
          </strong>
          <br />
          <strong>
            Date:{' '}
            <Typography variant="Small_Body_12">
              {moment().format('DD/MM/YYYY')}
            </Typography>
          </strong>
        </p>
        <div className="page-number">Page 5 of 5</div>
      </div>
    </div>
  );
};

export default ConvertPDFtoHTML;
